/**
 * Created for Plausible analytics tracking
 */
import { useEffect, useMemo, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { init, track } from '@plausible-analytics/tracker';
import { useRootStore } from '../Store/useRootStore';

const routeTitles: Record<string, string> = {
  '/': 'Home',
  '/home': 'Home',
  '/move': 'Movement',
  '/move/schedule': 'Schedule',
  '/move/library': 'On Demand',
  '/move/fitness-programs': 'Fitness Programs',
  '/mental-health': 'Mental Wellness',
  '/mental-health/library': 'Mental Health Library',
  '/mental-health/mindfulness-programs': 'Mindfulness Programs',
  '/nutrition': 'Nutrition',
  '/nutrition/library': 'Nutrition Library',
  '/academy': 'Academy',
  '/academy/schedule': 'Schedule',
  '/academy/library': 'Academy Library',
  '/motivation': 'Motivation',
  '/motivation/history': 'Points History',
  '/motivation/community': 'Community',
  '/motivation/challenges': 'Challenges',
  '/schedule': 'Calendar',
  '/activities': 'Activities',
  '/account': 'Account',
  '/feedback': 'Feedback',
  '/contact': 'Contact',
  '/privacy-policy': 'Privacy Policy',
  '/terms': 'Terms',
  '/blog': 'Blog',
  '/company': 'Company',
};

export function getTitleForPath(pathname: string): string | undefined {
  if (routeTitles[pathname]) return routeTitles[pathname];
  const base = '/' + pathname.split('/').filter(Boolean)[0];
  return routeTitles[base];
}

let plausibleInitialized = false;

export function usePlausibleTracking() {
  const { coachSubscription, gymCustomer, language } = useRootStore();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const noTracking = searchParams.has('noTracking');

  const trackingProperties = useMemo(() => {
    if (noTracking) return null;

    const props: Record<string, string> = {
      accessedPlatform: 'coach',
      language: language.language ?? 'en',
    };

    const plan = coachSubscription.current?.plan;
    if (plan) props.subscriptionType = plan;

    const title = getTitleForPath(pathname);
    if (title) props.pageTitle = title;

    const segment = pathname.split('/').filter(Boolean)[0];
    if (segment) props.contentType = segment;

    const gymId = gymCustomer.customer?.gymId;
    const gymName = gymCustomer.currentGym?.name;
    if (gymId) props.companyId = gymId;
    if (gymName) props.companyName = gymName;

    return props;
  }, [
    noTracking,
    language.language,
    coachSubscription.current?.plan,
    pathname,
    gymCustomer.customer?.gymId,
    gymCustomer.currentGym?.name,
  ]);

  useEffect(() => {
    if (!plausibleInitialized && trackingProperties) {
      plausibleInitialized = true;
      init({
        domain: 'educate.kinastic.com',
        endpoint: '/api/analytics',
        captureOnLocalhost: false
      });
    }
  }, [trackingProperties]);

  const trackPlausible = useCallback(
    (event: string, extra?: Record<string, string>) => {
      if (!trackingProperties) return;
      track(event, { props: { ...trackingProperties, ...extra } });
    },
    [trackingProperties],
  );

  return { trackingProperties, trackPlausible };
}
